import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api, ApiError } from '../../services/api';
import { SignedAgreement } from '../../types';

const formatDateTime = (iso: string, isArabic: boolean): string => {
  try {
    return new Date(iso).toLocaleString(isArabic ? 'ar-AE' : 'en-GB', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/**
 * Spec 071 Flow E / AC-15 — the owner's own signed agreements (newest first) with a download
 * per row. Shown only to GARAGE_OWNER while the switch is ON (SettingsPage decides). No IP or
 * user agent here: they are in the PDF only.
 */
export default function SignedAgreementsTab() {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const isArabic = lang === 'ar';
  const tr = useCallback(
    (key: string, fallback: string) => {
      const v = t(key);
      return v && v !== key ? v : fallback;
    },
    [t],
  );

  const [rows, setRows] = useState<SignedAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ id: string; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      setRows(await api.getMySignedAgreements(token));
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDownload = async (row: SignedAgreement) => {
    if (!token) return;
    setMessage(null);
    setDownloadingId(row.id);
    try {
      const { downloadUrl } = await api.getMySignedAgreementDownloadUrl(row.id, token);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      const pending = err instanceof ApiError && err.status === 409;
      setMessage({
        id: row.id,
        text: pending
          ? tr('settings.signedAgreements.pdfPending', 'The signed PDF is still being prepared. Try again in a few minutes.')
          : tr('settings.signedAgreements.downloadError', "Couldn't download the agreement. Please try again."),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const statusChip = (row: SignedAgreement) => {
    const current = row.status === 'CURRENT';
    return (
      <span className={`sa-chip ${current ? 'sa-chip-current' : 'sa-chip-superseded'}`}>
        {current
          ? tr('settings.signedAgreements.current', 'Current')
          : tr('settings.signedAgreements.superseded', 'Superseded')}
      </span>
    );
  };

  const originChip = (row: SignedAgreement) => {
    if (row.origin === 'RECONSTRUCTED') {
      return (
        <span className="sa-chip sa-chip-amber">
          {tr('settings.signedAgreements.reconstructed', 'Reconstructed')}
        </span>
      );
    }
    if (row.origin === 'NOTIFIED') {
      // Spec 074 — the terms update email with this version was delivered to the garage; no signature.
      return (
        <span className="sa-chip sa-chip-notified">
          {tr('settings.signedAgreements.notified', 'Sent by email')}
        </span>
      );
    }
    if (row.origin === 'UNVERIFIED') {
      return (
        <span className="sa-chip sa-chip-amber">
          {tr('settings.signedAgreements.unverified', 'Unverified')}
        </span>
      );
    }
    return null;
  };

  const title = tr('settings.signedAgreements.title', 'Signed agreements');

  return (
    <div className="settings-tab-content" data-testid="signed-agreements-tab">
      <div className="settings-section">
        <h3 className="settings-section-title">{title}</h3>

        {loading ? (
          <div className="settings-loading">{t('common.loading') || 'Loading...'}</div>
        ) : loadFailed ? (
          <div className="sa-load-error" role="alert">
            <span>
              {/* Not in the V30 seed list: falls back to the seeded signing load-error string. */}
              {tr(
                'settings.signedAgreements.loadError',
                tr('onboarding.terms.signature.loadError', "Couldn't load your signed agreements."),
              )}
            </span>
            <button type="button" className="settings-view-btn sa-btn" onClick={() => void load()}>
              {tr('onboarding.terms.signature.retry', 'Retry')}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="settings-empty-state">
            <p>{tr('settings.signedAgreements.empty', 'No signed agreements yet.')}</p>
          </div>
        ) : (
          <div className="sa-table-wrapper">
            <table className="settings-members-table sa-table">
              <caption className="sa-visually-hidden">{title}</caption>
              <thead>
                <tr>
                  <th scope="col">{tr('settings.signedAgreements.document', 'Document')}</th>
                  <th scope="col">{tr('settings.signedAgreements.version', 'Version')}</th>
                  <th scope="col">{tr('settings.signedAgreements.signer', 'Signer')}</th>
                  <th scope="col">{tr('settings.signedAgreements.agreedAt', 'Agreed at')}</th>
                  <th scope="col">{tr('settings.signedAgreements.status', 'Status')}</th>
                  <th scope="col">
                    <span className="sa-visually-hidden">
                      {tr('settings.signedAgreements.download', 'Download')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const docTitle = isArabic && row.documentTitleAr ? row.documentTitleAr : row.documentTitle;
                  const pdfReady = row.pdfStatus === 'STORED';
                  const busy = downloadingId === row.id;
                  const rowMessage = message?.id === row.id ? message.text : null;
                  const msgId = `sa-msg-${row.id}`;
                  return (
                    <tr key={row.id} data-testid="signed-agreement-row">
                      <td className="sa-doc">{docTitle}</td>
                      <td>v{row.version}</td>
                      <td dir="auto">{row.signerName || '—'}</td>
                      <td>{formatDateTime(row.agreedAt, isArabic)}</td>
                      <td>
                        <span className="sa-chip-row">
                          {statusChip(row)}
                          {originChip(row)}
                        </span>
                      </td>
                      <td className="sa-actions">
                        <button
                          type="button"
                          className="settings-view-btn sa-btn"
                          onClick={() => void handleDownload(row)}
                          disabled={busy || !pdfReady}
                          aria-busy={busy || undefined}
                          aria-label={`${tr('settings.signedAgreements.download', 'Download')} — ${docTitle} v${row.version}`}
                          aria-describedby={rowMessage || !pdfReady ? msgId : undefined}
                        >
                          {tr('settings.signedAgreements.download', 'Download')}
                        </button>
                        {(rowMessage || !pdfReady) && (
                          <span
                            id={msgId}
                            className="sa-row-message"
                            role={rowMessage ? 'status' : undefined}
                          >
                            {rowMessage ||
                              (row.pdfStatus === 'PENDING'
                                ? tr('settings.signedAgreements.pdfPending', 'The signed PDF is still being prepared. Try again in a few minutes.')
                                : tr('settings.signedAgreements.pdfUnavailable', 'No signed PDF is available for this record.'))}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
